import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatCurrency } from "@/utils/format/currency";
import { PrivateOfficeContractTableProps } from "./list-table-types";
import { safeFormatInTimeZone } from "@/utils/format/safe-format-in-time-zone";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Outlet, useNavigate } from "react-router-dom";
import { EditIcon, EyeIcon, Minus, MoveUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useList } from "@/features/private-room/contracts/list/context/use-list";
import {
  calculateTransferAmount,
  getStatusData,
} from "../../utils/private-office-contract";
import { Badge } from "@/components/ui/badge";

export const PrivateRoomContractsListTable = ({
  data,
  isLoading,
}: PrivateOfficeContractTableProps) => {
  const navigate = useNavigate();
  const { setPOContractToDetail } = useList();

  const tableHeaders = [
    {
      name: "ID",
      columnName: "id",
    },
    {
      name: "Empresa",
      columnName: "companyName",
    },
    {
      name: "Local / Detalhes",
      columnName: "spaceName",
    },
    {
      name: "Data início / fim",
      columnName: "startDate",
    },
    {
      name: "Status",
      columnName: "status",
    },
    {
      name: "ID Pacote",
      columnName: "package_id",
    },
    {
      name: "Créditos",
      columnName: "credits",
    },
    {
      name: "Take",
      columnName: "take",
    },
    {
      name: "Repasse",
      columnName: "transferAmount",
    },
    {
      name: "Data cancelamento",
      columnName: "cancelledAt",
    },
    {
      name: "Ações",
      columnName: "actions",
    },
  ];

  return (
    <div className="mt-3 bg-white rounded-lg border border-gray-200">
      <Outlet />
      <div className="max-h-[62vh] overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 rounded-t-lg">
            <TableRow className="sticky top-0 z-10">
              {tableHeaders.map((header, index) => (
                <TableHead
                  key={header.columnName}
                  className={`sticky top-0 z-20 ${index === 0 ? "px-10" : "py-4"}`}
                >
                  {header.name}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <>
                {Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell
                      colSpan={tableHeaders.length + 3}
                      className="px-10"
                    >
                      <Skeleton className="w-full h-[40px] rounded-full" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : (
              data?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="px-10">{item.id}</TableCell>
                  <TableCell className="font-medium py-5">
                    {item?.company?.name}
                  </TableCell>
                  <TableCell className="font-medium py-5">
                    {item?.space?.name} /{" "}
                    {item.type === "po"
                      ? item?.spot?.label
                      : `${item.type.toUpperCase()}`}
                  </TableCell>
                  <TableCell>
                    {safeFormatInTimeZone(item.startDate, "UTC", "dd/MM/yyyy")}
                    <br />
                    {safeFormatInTimeZone(
                      item.endDate,
                      "UTC",
                      "dd/MM/yyyy",
                      "Indeterminado",
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      style={{
                        backgroundColor: getStatusData(item.status).bgColor,
                      }}
                      className="text-gray-800 hover:opacity-80"
                    >
                      {getStatusData(item.status).label}
                    </Badge>
                  </TableCell>
                  <TableCell>{item.packageId}</TableCell>
                  <TableCell>{item.monthlyCredits.toFixed(2)}</TableCell>
                  <TableCell>{item.takePercentage}%</TableCell>
                  <TableCell>
                    {formatCurrency(
                      calculateTransferAmount(
                        item.takePercentage,
                        item.partnerPrice.toString(),
                      ),
                    )}
                  </TableCell>
                  <TableCell>
                    {safeFormatInTimeZone(
                      item.cancelledAt,
                      "UTC",
                      "dd/MM/yyyy",
                    ) ?? <Minus className="w-8 h-4" />}
                  </TableCell>
                  <TableCell>
                    <Menubar className="border-none shadow-none relative">
                      <Button
                        variant="outline"
                        onClick={() => setPOContractToDetail(item.id)}
                      >
                        <EyeIcon width={16} />
                      </Button>
                      {!["canceled"].includes(item.status || "") && (
                        <Button
                          variant="outline"
                          onClick={() =>
                            navigate(
                              `/private-office/contracts/update/${item.id}`,
                            )
                          }
                        >
                          <EditIcon width={16} />
                        </Button>
                      )}
                      <MenubarMenu>
                        <MenubarTrigger className="cursor-pointer">
                          <EllipsisHorizontalIcon width={20} />
                        </MenubarTrigger>
                        <MenubarContent>
                          <MenubarItem
                            onClick={() =>
                              navigate(
                                `/private-office/bookings?page=1&po_contract_id=${item.id}`,
                              )
                            }
                            className="cursor-pointer"
                          >
                            <MoveUpRight className="mr-2" width={16} />
                            Ver bookings
                          </MenubarItem>

                          {!["canceled", "inactive"].includes(
                            item.status || "",
                          ) &&
                            !item.cancelledAt && (
                              <>
                                <MenubarSeparator />
                                <MenubarItem
                                  className="text-red-600 cursor-pointer hover:text-red-600"
                                  onClick={() =>
                                    navigate(
                                      `/private-office/contracts/cancel/${item.id}`,
                                    )
                                  }
                                >
                                  <TrashIcon
                                    className="text-red-600 hover:text-red-400 cursor-pointer mr-2"
                                    width={16}
                                  />
                                  Cancelar
                                </MenubarItem>
                              </>
                            )}
                        </MenubarContent>
                      </MenubarMenu>
                    </Menubar>
                  </TableCell>
                </TableRow>
              ))
            )}
            {data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={tableHeaders.length + 3}>
                  <p className="font-bold mt-5 text-center text-lg">
                    Não foi encontrado nenhum contrato.
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
